/**
 * Yeni Proje — the three-step wizard.
 *
 * Step 1 asks what the user wants to scan - İnsan, Nesne, Mekân/Sahne - and
 * only then which kind. The six stored `subject_type` values did not change and
 * are not going to: backend capabilities are derived from them without
 * rewriting historical projects.
 *
 * It is also where the approved design is most tempting to lie. Its cards carry
 * maturity badges. All six are available because all six pass through the
 * generic capture, quality and sparse-reconstruction workflow. Missing
 * subject-specific semantics are stated in the detail panel instead.
 *
 * Step 3 creates real rows. The project is created first; if the user asked
 * for a first capture session, that is created second, and a failure there
 * leaves the project intact and says so rather than rolling back a project the
 * user successfully made.
 */

import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createCaptureSession, createProject } from "@/api/client";
import {
  CAPTURE_TYPES,
  type CaptureType,
  type GeometryMode,
  type SubjectType,
} from "@/api/types";
import { ApiError } from "@/api/errors";
import { Field } from "@/components/Field";
import { IconCheck, IconCube, IconFace, IconFiles, IconPerson, IconPhone, IconPlusSquare, IconTarget } from "@/components/icons";
import { PageHeader, Section } from "@/components/product";
import { Button, Callout } from "@/components/ui";
import {
  GEOMETRY_MODE_CHOICES,
  GEOMETRY_MODE_TERMS,
  CATEGORY_OF_SUBJECT,
  CAPTURE_TYPE_LABELS,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_TERMS,
  SUBJECT_TYPE_TERMS,
  type ProductCategory,
} from "@/i18n/terms";
import { paths } from "@/app/paths";
import "./projects.css";

const STEPS = ["Proje Türü", "Detaylar", "Başlangıç"] as const;

const START_CHOICES = [
  { id: "upload", label: "Fotoğraf Yükle", note: "Bilgisayarınızda hazır fotoğraflar varsa doğrudan yükleme ekranına geçin.", Icon: IconFiles },
  { id: "phone", label: "Telefonla Çekime Başla", note: "Yeni fotoğraflar çekmek için telefonunuzu bu projeyle eşleştirin.", Icon: IconPhone },
  { id: "empty", label: "Şimdilik Boş Proje", note: "Projeyi oluşturun; çekime daha sonra başlayın.", Icon: IconPlusSquare },
] as const;
type StartChoice = (typeof START_CHOICES)[number]["id"];

const CATEGORY_ICONS: Record<ProductCategory, typeof IconFace> = {
  human: IconPerson,
  object: IconCube,
  place: IconTarget,
};

/**
 * The maturity label, from real capability rather than from the mockup.
 *
 * Semantic coverage is not used as a proxy for the generic workflow: a room
 * without room-specific coverage is still a reconstruction-capable project.
 */
const NAME_EXAMPLES: Record<ProductCategory, string> = {
  human: "Örn. Portre çalışması",
  object: "Örn. Seramik vazo",
  place: "Örn. Oturma odası",
};

function Stepper({ current }: { current: number }) {
  return (
    <ol className="wizard__steps">
      {STEPS.map((label, index) => (
        <li
          key={label}
          className={`wizard__step${index === current ? " wizard__step--active" : ""}${
            index < current ? " wizard__step--done" : ""
          }`}
          aria-current={index === current ? "step" : undefined}
        >
          <span className="wizard__step-n">
            {index < current ? <IconCheck size={16} /> : String(index + 1).padStart(2, "0")}
          </span>
          <span>{label}</span>
        </li>
      ))}
    </ol>
  );
}

export function NewProjectScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [subjectType, setSubjectType] = useState<SubjectType | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);
  const [start, setStart] = useState<StartChoice | null>(null);
  const [captureType, setCaptureType] = useState<CaptureType>("photo");
  // Defaulted rather than left empty. The conservative answer is a real
  // answer, and a required question with no default would make somebody who
  // does not yet know what they want choose at random.
  const [geometryMode, setGeometryMode] = useState<GeometryMode>("precise");
  const [busy, setBusy] = useState(false);
  const submitLock = useRef(false);
  const [failure, setFailure] = useState<string | null>(null);

  const terms = subjectType ? SUBJECT_TYPE_TERMS[subjectType] : null;
  const selectedCategory = subjectType ? CATEGORY_OF_SUBJECT[subjectType] : null;
  const currentCategory = selectedCategory ? PRODUCT_CATEGORY_TERMS[selectedCategory] : null;
  const nameValid = name.trim().length > 0;

  const submit = useCallback(async () => {
    if (submitLock.current || !subjectType || !start) return;
    submitLock.current = true;
    setBusy(true);
    setFailure(null);
    try {
      const project = await createProject({
        name: name.trim(),
        subject_type: subjectType,
        // Only sent when the user wrote something; the API stores it for real.
        ...(description.trim() ? { description: description.trim() } : {}),
        geometry_mode: geometryMode,
      });
      if (start === "upload") {
        try {
          const session = await createCaptureSession(project.id, {
            capture_type: captureType,
            purpose: "initial",
          });
          navigate(paths.session(session.id), { replace: true });
          return;
        } catch {
          // The project exists. Saying otherwise, or unwinding it, would throw
          // away work the user successfully did.
          navigate(paths.projectSessions(project.id), { replace: true });
          return;
        }
      }
      if (start === "phone") {
        // The captures screen owns the authenticated, project-bound pairing
        // control. Avoid inventing a token or project id in the client.
        navigate(paths.projectSessions(project.id), { replace: true });
        return;
      }
      navigate(paths.project(project.id), { replace: true });
    } catch (error) {
      setFailure(
        error instanceof ApiError && error.status === 422
          ? "Proje bilgileri kabul edilmedi. Adı kontrol edip tekrar deneyin."
          : "Proje oluşturulamadı. Lütfen tekrar deneyin.",
      );
      setBusy(false);
      submitLock.current = false;
    }
  }, [name, subjectType, description, start, captureType, geometryMode, navigate]);

  return (
    <div className="stack">
      <PageHeader title="Yeni Proje" />
      <Stepper current={step} />

      {step === 0 && (
        <div className="wizard__stage">
          <div className="stack">
            {/*
              Three choices, because those are the three things people come
              here to scan. The six stored values still exist and still mean
              what they meant - they moved one level down, where they read as
              what kind of human or what kind of place rather than as six
              unrelated products.
            */}
            <h2 className="wizard__question">Ne taramak istiyorsunuz?</h2>
            <ul className="wizard__types">
              {PRODUCT_CATEGORIES.map((key) => {
                const category = PRODUCT_CATEGORY_TERMS[key];
                const Icon = CATEGORY_ICONS[key];
                const active = selectedCategory === key;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      className={`wizard__type${active ? " wizard__type--active" : ""}`}
                      aria-pressed={active}
                      onClick={() => {
                        // Selecting a category picks its first subtype, so the
                        // wizard always holds a value the API accepts.
                        const [first] = category.subjectTypes;
                        if (first) setSubjectType(first);
                      }}
                    >
                      <Icon size={28} />
                      <span className="wizard__type-body">
                        <span className="wizard__type-name">{category.label}</span>
                        <span className="wizard__type-note">{category.description}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/*
              Only shown where there is a real choice to make. A category with
              one subtype would be a radio group of one, which is a control
              that asks a question with a single answer.
            */}
            {currentCategory && currentCategory.subjectTypes.length > 1 && (
              <fieldset className="wizard__subtypes">
                <legend className="wizard__question">
                  {currentCategory.label} — hangi tür?
                </legend>
                {currentCategory.subjectTypes.map((type) => {
                  return (
                    <label key={type} className="wizard__subtype">
                      <input
                        type="radio"
                        name="subject-subtype"
                        value={type}
                        checked={subjectType === type}
                        onChange={() => setSubjectType(type)}
                      />
                      <span className="wizard__subtype-name">
                        {SUBJECT_TYPE_TERMS[type].subtypeLabel}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}
          </div>

          {terms ? <Section title={terms.label}>
            <p className="muted">{terms.analysisNote}</p>
            <ul className="help__list">
              {terms.capabilityNotes.map((note) => <li key={note}>{note}</li>)}
            </ul>
            <Callout tone="neutral">
              Rekonstrüksiyon sonucu büyük ölçüde çekimin kendisine bağlıdır; belirli
              bir çıktı kalitesi garanti edilmez.
            </Callout>
          </Section> : <Section title="Önce bir konu seçin" description="Seçiminiz çekim önerilerini ve proje ayarlarını belirler."><p className="muted">İnsan, nesne veya mekân seçeneklerinden birini seçerek devam edin.</p></Section>}
        </div>
      )}

      {step === 1 && (
        <div className="wizard__column">
          <Section
            title="Proje Bilgileri"
            description="Projenizi tanımlamak için temel bilgileri girin."
          >
            <div className="form">
              <Field
                label="Proje adı"
                required
                hint="Listelerde ve proje başlığında bu ad gösterilir."
                // The message belongs to the field, not next to it: `Field`
                // wires `aria-describedby` and `aria-invalid`, which a loose
                // paragraph underneath never did.
                error={touched && !nameValid ? "Proje adı gerekli." : null}
              >
                {(props) => (
                  <input
                    {...props}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder={selectedCategory ? NAME_EXAMPLES[selectedCategory] : "Örn. Yeni projem"}
                    autoFocus
                  />
                )}
              </Field>
              <Field
                label="Açıklama"
                optional
                hint="Projenin amacını veya çekim notlarını kısaca yazabilirsiniz."
              >
                {(props) => (
                  <textarea
                    {...props}
                    rows={5}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </Section>

          {/*
            Asked once, here, because there is no default that serves both
            answers and picking one silently is how a measurement becomes a
            guess. Two sentences, no jargon: the words "provenance", "mesh" and
            "AI completion" do not appear, and neither does a percentage.
          */}
          <Section
            title="Bu modeli ne için kullanacaksınız?"
            description="Bu seçim, eksik kalan yüzeylerin ne olacağını belirler. Daha sonra değiştirebilirsiniz."
          >
            <ul
              className="wizard__types wizard__types--start"
              role="radiogroup"
              aria-label="Model kullanım amacı"
            >
              {GEOMETRY_MODE_CHOICES.map(({ value, label, note }) => (
                <li key={value}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={geometryMode === value}
                    className={`wizard__type${geometryMode === value ? " wizard__type--active" : ""}`}
                    onClick={() => setGeometryMode(value)}
                  >
                    <span className="wizard__type-body">
                      <span className="wizard__type-name">{label}</span>
                      <span className="wizard__type-note">{note}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}

      {step === 2 && (
        <Section title="Nasıl başlamak istiyorsunuz?">
          <ul className="wizard__types wizard__types--start" role="radiogroup" aria-label="Başlangıç yöntemi">
            {START_CHOICES.map(({ id, label, note, Icon }) => (
              <li key={id}>
                <button type="button" role="radio" aria-checked={start === id}
                  className={`wizard__type${start === id ? " wizard__type--active" : ""}`}
                  onClick={() => setStart(id)}>
                  <Icon size={28} />
                  <span className="wizard__type-body"><span className="wizard__type-name">{label}</span><span className="wizard__type-note">{note}</span></span>
                </button>
              </li>
            ))}
          </ul>
          {start === "upload" && (
            <Field label="Çekim türü" hint="Bu oturuma ne yükleyeceksiniz?">
              {(props) => (
                <select
                  {...props}
                  value={captureType}
                  onChange={(event) => setCaptureType(event.target.value as CaptureType)}
                >
                  {CAPTURE_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {CAPTURE_TYPE_LABELS[value]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <dl className="wizard__summary">
            <div>
              <dt>Konu türü</dt>
              <dd>{terms?.label ?? "—"}</dd>
            </div>
            <div>
              <dt>Proje adı</dt>
              <dd>{name.trim() || "—"}</dd>
            </div>
            <div>
              <dt>Kullanım amacı</dt>
              <dd>{GEOMETRY_MODE_TERMS[geometryMode].label}</dd>
            </div>
          </dl>

          {failure && (
            <p className="pairing-badge pairing-badge--warn" role="alert">
              <span aria-hidden="true">!</span> {failure}
            </p>
          )}
        </Section>
      )}

      {/* On the form step the actions belong to the form column, not to the
          far edge of a 1920px viewport. */}
      <div className={`wizard__actions${step === 1 ? " wizard__actions--column" : ""}`}>
        <Button
          variant="ghost"
          onClick={() => (step === 0 ? navigate(paths.projects()) : setStep(step - 1))}
          disabled={busy}
        >
          {step === 0 ? "İptal" : "Geri"}
        </Button>
        {step < 2 ? (
          <Button
            variant="primary"
            onClick={() => {
              if (step === 1) setTouched(true);
              if (step === 1 && !nameValid) return;
              setStep(step + 1);
            }}
            disabled={(step === 0 && !subjectType) || (step === 1 && !nameValid)}
          >
            Devam Et
          </Button>
        ) : (
          <Button variant="primary" busy={busy} disabled={!start} onClick={() => void submit()}>
            Projeyi Oluştur
          </Button>
        )}
      </div>
    </div>
  );
}
